import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { breadcrumbParts } from "../../../models/breadcrumb-parts";
import { BreadcrumbList } from "../../ui/breadcrumb-list/breadcrumb-list";
import { Header } from "../../ui/header/header";
import { Input } from "../../ui/input/input";
import { SideBar } from "../../ui/sidebar/sidebar";
import { SortListType, Table } from "../../table/table";
import { Pager } from "../../ui/pager/pager";
import { useDispatch } from "react-redux";
import { apiIsp, ApiManager } from "../../../managers/api-manager";
import { Button } from "../../ui/button/button";
import './company.scss';
import { useAsyncLogOperation } from "../../../hooks/use-async-log-operation";
import { Checkbox } from "../../ui/checkbox/checkbox";
import { ApiDownloadCsvDeleteCompanyRequest, ApiGetDeleteCompanyListResponse, ApiGetDeleteCompanyRequest } from "../../../module/api/api/isp-web/delete-company";
import { IspLogOperation } from "../../../models/logs/isp-log-operation";

export type HasBlurElement = {
  blur: () => void,
}


const formatDate = (date: string) => {
  if (!date) return '-';
  return date.replaceAll('-', '/').slice(0, 10);
}

const breadcrumbList = [
  breadcrumbParts.isp.home,
  breadcrumbParts.isp.companyDelete,
];

const sortKey = {
  company_code: 1,
  stoppedAt: 2,
  limitedAt: 3,
  createdAt: 4,
  immediateAt: 5,
  deleteAt: 6,
};

const highlowKey = {
  high: 0,
  low: 1,
};

const initialSortList: SortListType[] = [
  {
    index: 0,
    type: 'up',
    key: sortKey.company_code,
    active: '',
    callback: () => {
    },
  },
  {
    index: 2,
    type: 'up',
    key: sortKey.stoppedAt,
    active: '',
    callback: () => {
    },
  },
  {
    index: 3,
    type: 'up',
    key: sortKey.limitedAt,
    active: '',
    callback: () => {
    },
  },
  {
    index: 4,
    type: 'up',
    key: sortKey.createdAt,
    active: '',
    callback: () => {
    },
  },
  {
    index: 6,
    type: 'up',
    key: sortKey.immediateAt,
    active: '',
    callback: () => {
    },
  },
  {
    index: 8,
    type: 'up',
    key: sortKey.deleteAt,
    active: '',
    callback: () => {
    },
  },
];

// テーブル要素用意
const head: string[] = [
  '企業ID',
  '企業名',
  '利用停止日',
  '保存期間終了日',
  'データ削除登録日',
  'データ削除登録者',
  '即時削除登録日',
  '即時削除登録者',
  '削除実施日',
];

export const CompanyDelete = () => {
  const [companies, setCompanies] = useState<ApiGetDeleteCompanyListResponse[]>([]);
  const [sortList, setSortList] = useState<SortListType[]>(initialSortList);
  
  const rowRef = useRef<HTMLTableRowElement>(null);
  const [viewDelete, setViewDelete] = useState(false);
  const [sortBy, setSortBy] = useState(sortKey.createdAt);
  const [highlow, setHighlow] = useState(highlowKey.high);
  const [index, setIndex] = useState(4);
  const [activeSort, setActiveSort] = useState('active');
  const [radioValue, setRadioValue] = useState(0);
  const [hitCount, setHitCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [perPage, setPerPage] = useState(20);

  //　表示データの全件数
  // 一度に表示するデータの件数
  // 現在のページ
  const [currentPage, setCurrentPage] = useState(0);
  // 表示開始のデータ番号
  const totalPage: number = useMemo(() => {
    if (!totalCount) return 0;
    const _totalPage = totalCount % perPage ?
      Math.floor(totalCount / perPage) : // 合計のページ数
      Math.floor(totalCount / perPage) - 1; // 合計のページ数(件数が丁度ページいっぱいで収まる時)
    return _totalPage;
  }, [totalCount, perPage]);

  const body = useMemo(() =>
    companies.map(element => ([
      element.company_code,
      element.company_name,
      formatDate(element.stoped_date),
      formatDate(element.limit_date),
      formatDate(element.updated_at),
      element.user_name,
      formatDate(element.immediate_at),
      element.immediate_user_name,
      formatDate(element.deleted_at),
      element.deleted_at ? 'stop' : 'active',
    ])),
    [companies]
  );

  const getList = useCallback((req?: Partial<ApiGetDeleteCompanyRequest>) => {
    const _req: ApiGetDeleteCompanyRequest = {
      is_view: viewDelete ? 1 : 0,
      kind: radioValue,
      limit: 2,
      sort_by: sortBy,
      highlow,
      page: currentPage + 1,
      ...req,
    }
    apiIsp.deleteCompany().get(_req).then((res) => {
      if (String(res.header.status_code) === '200') {
        const { data } = res.body;
        if (data) {
          setHitCount(res.body.hit_count);
          setTotalCount(res.body.total);
          setPerPage(Number(res.body.per_page));
          setCompanies(data);
        }
      }
    }).catch((e) => {
      ApiManager.errorFunc(e);
    });
  }, [
    currentPage,
    highlow,
    sortBy,
    radioValue,
    viewDelete,
  ]);

    /** 表示するページをセット */
  const handlePaginate = useCallback((page: number) => {
    setCurrentPage(page);
    getList({ page: page + 1 });
  }, [
    getList
  ]);

  const handlerSort = useCallback((arg: { sortBy: number, highlow: number }) => {
    setSortBy(arg.sortBy);
    setHighlow(arg.highlow);
    getList({
      sort_by: arg.sortBy,
      highlow: arg.highlow,
    })
  }, [
    getList,
  ]);

  const handlerChangeView = useCallback((param: {
    kind?: number,
    deleteView?: boolean,
  }) => {
    setRadioValue(param.kind ?? radioValue);
    setViewDelete(param.deleteView ?? viewDelete);
    getList({
      kind: param.kind ?? radioValue,
      is_view: (param.deleteView ?? viewDelete) ? 1 : 0,
    })
  }, [
    radioValue,
    viewDelete,
    getList,
  ]);

  const handlerDLCSV = useCallback(() => {
    const req: ApiDownloadCsvDeleteCompanyRequest = {
      is_view: viewDelete ? 1 : 0,
      kind: radioValue,
      sort_by: sortBy,
      highlow,
    }
    IspLogOperation('openDeleteCompanyListDl');
    apiIsp.deleteCompany().download(req).then((blob) => {
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = '削除予定一覧.csv';
      anchor.click();
      URL.revokeObjectURL(url);
    });
  }, [
    highlow,
    sortBy,
    radioValue,
    viewDelete,
  ]);

    // クリックされたソートを書き換える
  const updateSortList = useCallback(() => {
    setSortList(prevSortList =>
      prevSortList.map(elm =>
        elm.index === index
          ? {
              ...elm,
              type: highlow ? 'down' : 'up',
              active: activeSort ? 'active' : '',
            }
          : {
            ...elm,
            active: '',
          }
      )
    );
  }, [index, highlow, activeSort]);
  useEffect(() => {
    updateSortList();
  }, [updateSortList]);

  useEffect(() => {
    getList();
  }, []);

  return(
    <div
      id="App"
      className="company_delete"
    >
      <SideBar currentPage="company-delete" />
      <div className="main_cnt">
        <Header />
        <div className="inner">
          <BreadcrumbList breadcrumbList={breadcrumbList} />
          <section>
            <header>
              <h2>
                削除予定一覧
              </h2>
              <div className="count">
                <span>件数</span>
                {totalCount}
                <span>件</span>
              </div>
              <div className='company_delete__header__right'>
                <div style={{ display: 'flex', justifyContent: 'end', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', justifyContent: 'end', paddingRight: '15px' }}>
                    <Checkbox label="削除実施済の企業も表示する" id="delete_comp_company" checked={viewDelete} onClick={() => handlerChangeView({ deleteView: !viewDelete})} />
                  </div>
                  <div>
                    <div className={`radio_style`}>
                      <Input
                        id="delete_0"
                        type="radio"
                        checked={radioValue === 0}
                        onClick={() => {
                          handlerChangeView({
                            kind: 0,
                          })
                        }}
                        name="immediately"
                      />
                      <label htmlFor="delete_0">即時削除と定期削除</label>
                    </div>
                    <div className={`radio_style`}>
                      <Input
                        id="delete_1"
                        type="radio"
                        checked={radioValue === 1}
                        onClick={() => {
                          handlerChangeView({
                            kind: 1,
                          })
                        }}
                        name="immediately"
                      />
                      <label htmlFor="delete_1">即時削除のみ</label>
                    </div>
                    <div className={`radio_style`}>
                      <Input
                        id="delete_2"
                        type="radio"
                        checked={radioValue === 2}
                        onClick={() => {
                          handlerChangeView({
                            kind: 2,
                          })
                        }}
                        name="immediately"
                      />
                      <label htmlFor="delete_2">定期削除のみ</label>
                    </div>
                  </div>            
                </div>
                <Button label="DL" onClick={handlerDLCSV} />
              </div>
            </header>
            <>
              <div className="table_box">
                <Table
                  head={head}
                  body={body}
                  alignList={['c', 'l', 'c', 'c', 'c', 'c', 'c', 'c', 'c']}
                  sortList={sortList}
                  setSortBy={() => {}}
                  setHighlow={() => {}}
                  setBulkSort={handlerSort}
                  setIndex={setIndex}
                  setActive={setActiveSort}
                  rowRef={rowRef}
                />
              </div>
              <Pager
                totalPage={totalPage}
                currentPage={currentPage}
                callback={handlePaginate}
                onChange={() => {}}
              />
            </>
          </section>
        </div>
      </div>
    </div>
  );
};
